import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';

import { usePosthogCapture } from 'events/posthogCapture/_index';
import * as Styled from 'events/styles/DaySummary.style';

import { HANDOVER_PREFERENCE_OPTIONS } from './handoverPreferenceOptions';

export interface EditPreferencesViewProps {
  initialPreferences: string[];
  isSaving?: boolean;
  onSave: (preferences: string[]) => void;
}

export default function EditPreferencesView({
  initialPreferences,
  isSaving = false,
  onSave,
}: EditPreferencesViewProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();
  const [selected, setSelected] = useState<string[]>(initialPreferences);

  useEffect(() => {
    setSelected(initialPreferences);
  }, [initialPreferences]);

  const togglePreference = (value: string) => {
    setSelected((prev) => {
      const isSelected = prev.includes(value);
      if (isSelected) {
        capture.daySummaryEditPreferencesOptionDeselected({
          preference: value,
        });
        return prev.filter((v) => v !== value);
      }
      capture.daySummaryEditPreferencesOptionSelected({ preference: value });
      return [...prev, value];
    });
  };

  const handleSave = () => {
    capture.daySummaryEditPreferencesSaved({ count: selected.length });
    onSave(selected);
  };

  return (
    <>
      <Styled.PreferencesHeader>
        <Styled.PreferencesTitle>
          {t('daySummary.editPreferences.title')}
        </Styled.PreferencesTitle>
        <Styled.PreferencesSubtitle>
          {t('daySummary.editPreferences.subtitle')}
        </Styled.PreferencesSubtitle>
      </Styled.PreferencesHeader>
      <Styled.PreferencesChipGrid>
        {HANDOVER_PREFERENCE_OPTIONS.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <Styled.PreferencesChip
              key={option.value}
              type="button"
              $selected={isSelected}
              aria-pressed={isSelected}
              onClick={() => togglePreference(option.value)}
            >
              <Styled.PreferencesChipLabel $selected={isSelected}>
                {t(`daySummary.editPreferences.options.${option.labelKey}`)}
              </Styled.PreferencesChipLabel>
              <Styled.PreferencesChipCheckbox $selected={isSelected}>
                {isSelected && <General.Check />}
              </Styled.PreferencesChipCheckbox>
            </Styled.PreferencesChip>
          );
        })}
      </Styled.PreferencesChipGrid>
      <Styled.PreferencesSaveButton
        type="button"
        onClick={handleSave}
        disabled={isSaving}
      >
        {t('daySummary.editPreferences.save')}
      </Styled.PreferencesSaveButton>
    </>
  );
}
